import { ABILITIES, CLASSES, HERO_ORDER, autoFor, STATUSES, binds } from "../content";
import { bindLabel } from "../content/tuning";
import {
  ARENA_H,
  ARENA_W,
  SCORE_LIMIT,
  shieldTotal,
  type HeroClassId,
  type Unit,
  type World,
  occupants,
} from "../sim";
import { type Camera, pixelsPerUnit, worldToScreen } from "./camera";

export type Phase = "pick" | "match" | "result";

export interface PickState {
  classId: HeroClassId;
}

export interface RenderFrame {
  phase: Phase;
  pick: PickState;
  world: World | null;
  cam: Camera;
  viewW: number;
  viewH: number;
  hover: { x: number; y: number };
  showBoard: boolean;
}

const TEAM: [string, string] = ["#4da3ff", "#ff5a5a"];
const TEAM_DIM: [string, string] = ["#245a8c", "#8a3030"];

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

function fillText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, color: string, align: CanvasTextAlign = "left", weight = "600"): void {
  ctx.font = `${weight} ${size}px "Trebuchet MS", "Segoe UI", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y);
}

export function render(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const { viewW, viewH } = frame;
  ctx.clearRect(0, 0, viewW, viewH);
  const bg = ctx.createLinearGradient(0, 0, 0, viewH);
  bg.addColorStop(0, "#121821");
  bg.addColorStop(1, "#0b0e13");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, viewW, viewH);

  if (frame.phase === "pick") renderPick(ctx, frame);
  else if (frame.phase === "match" && frame.world) renderMatch(ctx, frame, frame.world);
  else if (frame.phase === "result" && frame.world) {
    renderMatch(ctx, frame, frame.world);
    renderResult(ctx, frame, frame.world);
  }
}

function pickLayout(viewW: number, viewH: number) {
  const cols = 5;
  const rows = 2;
  const gap = 12;
  const cardW = Math.min(188, (viewW - 80 - gap * (cols - 1)) / cols);
  const cardH = Math.min(118, (viewH - 340) / rows);
  const total = cols * cardW + (cols - 1) * gap;
  const x0 = (viewW - total) / 2;
  const titleY = 36;
  const gridY = titleY + 70;
  const lawY = gridY + rows * (cardH + gap) + 10;
  const bw = 240;
  const bh = 48;
  const startY = Math.min(viewH - 70, lawY + 118);
  return { cols, rows, gap, cardW, cardH, x0, titleY, gridY, lawY, bw, bh, startY, startX: (viewW - bw) / 2 };
}

function renderPick(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const { viewW, pick } = frame;
  const L = pickLayout(viewW, frame.viewH);
  fillText(ctx, "COURT", viewW / 2, L.titleY, 44, "#f2e6c4", "center", "700");
  fillText(ctx, "Pick a class kit. Auto, Skill, Ult. Conditions and Enchantments are first-class.", viewW / 2, L.titleY + 34, 14, "#9aa4b2", "center", "500");

  HERO_ORDER.forEach((id, i) => {
    const col = i % L.cols;
    const row = Math.floor(i / L.cols);
    drawClassCard(ctx, L.x0 + col * (L.cardW + L.gap), L.gridY + row * (L.cardH + L.gap), L.cardW, L.cardH, id, pick.classId === id);
  });

  const c = CLASSES[pick.classId];
  const auto = ABILITIES[autoFor(pick.classId, 0)];
  const skill = ABILITIES[c.skill];
  const ult = ABILITIES[c.ult];
  rr(ctx, L.x0, L.lawY, viewW - L.x0 * 2, 108, 10);
  ctx.fillStyle = "#161c24";
  ctx.fill();
  ctx.strokeStyle = c.color;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  fillText(ctx, c.name.toUpperCase() + "  ·  " + c.role, L.x0 + 16, L.lawY + 18, 14, c.color, "left", "800");
  wrap(ctx, c.law, L.x0 + 16, L.lawY + 34, viewW - L.x0 * 2 - 32, 12, "#c8c2b4");
  fillText(ctx, `Auto  ${auto.name}    ·    ${bindLabel(binds.skill)}  ${skill.name}    ·    ${bindLabel(binds.ult)}  ${ult.name}`, L.x0 + 16, L.lawY + 90, 12, "#9aa4b2", "left", "600");

  rr(ctx, L.startX, L.startY, L.bw, L.bh, 10);
  ctx.fillStyle = "#d4a017";
  ctx.fill();
  fillText(ctx, "ENTER THE COURT", L.startX + L.bw / 2, L.startY + L.bh / 2, 16, "#1a1408", "center", "800");
}

function drawClassCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  id: HeroClassId,
  selected: boolean,
): void {
  const p = CLASSES[id];
  rr(ctx, x, y, w, h, 10);
  ctx.fillStyle = selected ? "#243044" : "#1a2029";
  ctx.fill();
  ctx.strokeStyle = selected ? p.color : "#2c3542";
  ctx.lineWidth = selected ? 2.4 : 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x + 18, y + 22, 8, 0, Math.PI * 2);
  ctx.fillStyle = p.color;
  ctx.fill();
  fillText(ctx, p.name, x + 34, y + 22, 15, "#f0ebe0", "left", "700");
  fillText(ctx, p.role, x + 12, y + 46, 11, p.color, "left", "700");
  wrap(ctx, p.law, x + 12, y + 62, w - 22, 10, "#8b93a0");
}

function wrap(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, size: number, color: string): void {
  ctx.font = `500 ${size}px "Trebuchet MS", "Segoe UI", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const words = text.split(" ");
  let line = "";
  let yy = y;
  let lines = 0;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += size + 3;
      lines += 1;
      if (lines >= 3) {
        ctx.fillText(line + "…", x, yy);
        return;
      }
    } else line = test;
  }
  if (line) ctx.fillText(line, x, yy);
}

export interface HitRegion {
  kind: "class" | "start" | "again";
  id?: HeroClassId;
  x: number;
  y: number;
  w: number;
  h: number;
}

export function pickHitRegions(viewW: number, viewH: number): HitRegion[] {
  const L = pickLayout(viewW, viewH);
  const regions: HitRegion[] = [];
  HERO_ORDER.forEach((id, i) => {
    const col = i % L.cols;
    const row = Math.floor(i / L.cols);
    regions.push({
      kind: "class",
      id,
      x: L.x0 + col * (L.cardW + L.gap),
      y: L.gridY + row * (L.cardH + L.gap),
      w: L.cardW,
      h: L.cardH,
    });
  });
  regions.push({ kind: "start", x: L.startX, y: L.startY, w: L.bw, h: L.bh });
  return regions;
}

export function resultHitRegions(viewW: number, viewH: number): HitRegion[] {
  return [{ kind: "again", x: (viewW - 220) / 2, y: viewH / 2 + 92, w: 220, h: 44 }];
}

function renderMatch(ctx: CanvasRenderingContext2D, frame: RenderFrame, world: World): void {
  const { cam, viewW, viewH } = frame;
  const ppu = pixelsPerUnit(viewW, viewH);
  const origin = worldToScreen(0, 0, cam, viewW, viewH);

  drawArena(ctx, origin.x, origin.y, ppu);
  drawZones(ctx, world, cam, viewW, viewH, ppu);
  drawStones(ctx, world, cam, viewW, viewH, ppu);
  drawCover(ctx, world, cam, viewW, viewH, ppu);
  drawPulses(ctx, world, cam, viewW, viewH, ppu);
  drawDelays(ctx, world, cam, viewW, viewH, ppu);
  drawProjectiles(ctx, world, cam, viewW, viewH, ppu);
  const order = [...world.units].sort((a, b) => a.pos.y - b.pos.y);
  for (const u of order) drawUnit(ctx, u, world, cam, viewW, viewH, ppu);
  drawTargetLock(ctx, world, cam, viewW, viewH, ppu);
  drawEvents(ctx, world, cam, viewW, viewH);

  drawChrome(ctx, world, viewW, viewH, frame.showBoard);
  drawMinimap(ctx, world, viewW, viewH);
}

function drawArena(ctx: CanvasRenderingContext2D, ox: number, oy: number, ppu: number): void {
  const w = ARENA_W * ppu;
  const h = ARENA_H * ppu;
  rr(ctx, ox - 10, oy - 10, w + 20, h + 20, 14);
  ctx.fillStyle = "#1b140f";
  ctx.fill();

  ctx.save();
  rr(ctx, ox, oy, w, h, 8);
  ctx.clip();
  const floor = ctx.createLinearGradient(ox, oy, ox, oy + h);
  floor.addColorStop(0, "#2a3342");
  floor.addColorStop(1, "#232a36");
  ctx.fillStyle = floor;
  ctx.fillRect(ox, oy, w, h);

  ctx.strokeStyle = "rgba(255,255,255,0.035)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= ARENA_W; x += 2) {
    ctx.beginPath();
    ctx.moveTo(ox + x * ppu, oy);
    ctx.lineTo(ox + x * ppu, oy + h);
    ctx.stroke();
  }
  for (let y = 0; y <= ARENA_H; y += 2) {
    ctx.beginPath();
    ctx.moveTo(ox, oy + y * ppu);
    ctx.lineTo(ox + w, oy + y * ppu);
    ctx.stroke();
  }
  ctx.fillStyle = "rgba(77,163,255,0.06)";
  ctx.fillRect(ox, oy, 3.2 * ppu, h);
  ctx.fillStyle = "rgba(255,90,90,0.06)";
  ctx.fillRect(ox + w - 3.2 * ppu, oy, 3.2 * ppu, h);
  ctx.restore();
  ctx.strokeStyle = "#6a5644";
  ctx.lineWidth = 3;
  rr(ctx, ox, oy, w, h, 8);
  ctx.stroke();
}

function toS(x: number, y: number, cam: Camera, viewW: number, viewH: number): { x: number; y: number } {
  return worldToScreen(x, y, cam, viewW, viewH);
}

function drawZones(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const z of world.zones) {
    const p = toS(z.pos.x, z.pos.y, cam, viewW, viewH);
    const r = z.radius * ppu;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = z.kind === "smoke" ? "rgba(160,170,180,0.22)" : "rgba(230,200,110,0.16)";
    ctx.fill();
    ctx.strokeStyle = z.kind === "smoke" ? "rgba(200,210,220,0.45)" : TEAM[z.team];
    ctx.lineWidth = z.kind === "hallowed" ? 4 : 2;
    ctx.stroke();
  }
}

function drawDelays(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const d of world.delays) {
    const p = toS(d.pos.x, d.pos.y, cam, viewW, viewH);
    ctx.beginPath();
    ctx.arc(p.x, p.y, d.radius * ppu, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(240,226,168,0.7)";
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

function drawStones(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const s of world.stones) {
    const p = toS(s.pos.x, s.pos.y, cam, viewW, viewH);
    const r = s.radius * ppu;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    const col = s.owner === null ? "rgba(201,162,39,0.18)" : s.owner === 0 ? "rgba(77,163,255,0.22)" : "rgba(255,90,90,0.22)";
    ctx.fillStyle = col;
    ctx.fill();
    ctx.strokeStyle = s.owner === null ? "#c9a227" : TEAM[s.owner];
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
    ctx.fillStyle = s.owner === null ? "#e6c35c" : TEAM[s.owner];
    ctx.fill();
    if (s.progress > 0 && s.capturing !== null) {
      ctx.beginPath();
      ctx.strokeStyle = TEAM[s.capturing];
      ctx.lineWidth = 3;
      ctx.arc(p.x, p.y, r - 4, -Math.PI / 2, -Math.PI / 2 + s.progress * Math.PI * 2);
      ctx.stroke();
    }
    fillText(ctx, s.name.toUpperCase(), p.x, p.y - r - 10, 11, "#d7d0c0", "center", "700");
  }
}

function drawCover(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const c of world.cover) {
    if (c.destroyed) continue;
    const p = toS(c.x, c.y, cam, viewW, viewH);
    const ratio = c.hp / c.maxHp;
    rr(ctx, p.x, p.y, c.w * ppu, c.h * ppu, 4);
    ctx.fillStyle = ratio > 0.5 ? "#6b5344" : ratio > 0.25 ? "#5a4034" : "#4a3028";
    ctx.fill();
    ctx.strokeStyle = "#3a281e";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

function drawPulses(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const pul of world.pulses) {
    const p = toS(pul.pos.x, pul.pos.y, cam, viewW, viewH);
    const t = 1 - pul.life / pul.maxLife;
    const r = pul.radius * ppu * (0.55 + t * 0.55);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    const alpha = 0.35 * (1 - t);
    ctx.strokeStyle = pul.kind === "heal" ? `rgba(140,220,160,${alpha})` : pul.kind === "slam" ? `rgba(220,180,80,${alpha})` : `rgba(255,140,80,${alpha})`;
    ctx.lineWidth = 3;
    ctx.stroke();
  }
}

function drawProjectiles(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  for (const pr of world.projectiles) {
    const p = toS(pr.pos.x, pr.pos.y, cam, viewW, viewH);
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(3.5, pr.radius * ppu), 0, Math.PI * 2);
    ctx.fillStyle = TEAM[pr.team];
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.6)";
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function drawUnit(ctx: CanvasRenderingContext2D, u: Unit, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  const p = toS(u.pos.x, u.pos.y, cam, viewW, viewH);
  const r = u.radius * ppu;
  const cls = CLASSES[u.classId];

  if (u.kind === "corpse" && u.dead) {
    ctx.globalAlpha = 0.4;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + 3, r * 1.1, r * 0.55, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#555";
    ctx.fill();
    fillText(ctx, `${Math.ceil(u.respawnIn)}`, p.x, p.y, 11, "#ccc", "center");
    ctx.globalAlpha = 1;
    return;
  }

  const hidden = u.statuses.some((s) => s.id === "invisible");
  if (hidden) ctx.globalAlpha = u.isPlayer ? 0.45 : 0.18;

  if (u.lockout > 0 && u.statuses.some((s) => s.id === "stun" || s.id === "stagger")) {
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + 4, r * 1.15, r * 0.55, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fill();
  }

  if (u.kind === "spirit") {
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(159,216,228,0.55)";
    ctx.fill();
    ctx.strokeStyle = "#9fd8e4";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(p.x, p.y, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = TEAM_DIM[u.team];
    ctx.fill();
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = u.kind === "corpse" ? "#6a3a48" : TEAM[u.team];
    ctx.fill();
    ctx.strokeStyle = cls.color;
    ctx.lineWidth = 2.4;
    ctx.stroke();
  }

  const nose = r + 7;
  ctx.beginPath();
  ctx.moveTo(p.x + Math.cos(u.facing) * nose, p.y + Math.sin(u.facing) * nose);
  ctx.lineTo(p.x + Math.cos(u.facing + 2.5) * (r * 0.55), p.y + Math.sin(u.facing + 2.5) * (r * 0.55));
  ctx.lineTo(p.x + Math.cos(u.facing - 2.5) * (r * 0.55), p.y + Math.sin(u.facing - 2.5) * (r * 0.55));
  ctx.closePath();
  ctx.fillStyle = "#f4efe4";
  ctx.fill();

  if (u.casting || (u.charging && u.charging.remaining > 0)) {
    const rem = u.casting?.remaining ?? u.charging!.remaining;
    const tot = u.casting?.total ?? 0.75;
    const t = 1 - rem / Math.max(0.01, tot);
    ctx.beginPath();
    ctx.strokeStyle = "#f0e6c8";
    ctx.lineWidth = 3;
    ctx.arc(p.x, p.y, r + 9, -Math.PI / 2, -Math.PI / 2 + t * Math.PI * 2);
    ctx.stroke();
  }

  const bw = 36;
  const bh = 4;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(p.x - bw / 2, p.y - r - 16, bw, bh);
  ctx.fillStyle = u.hp / u.maxHp < 0.25 ? "#ff5a5a" : u.team === 0 ? "#5ad48a" : "#ff8a6a";
  ctx.fillRect(p.x - bw / 2, p.y - r - 16, bw * Math.max(0, u.hp / u.maxHp), bh);
  const sh = shieldTotal(u);
  if (sh > 0) {
    ctx.fillStyle = "#7ec8ff";
    ctx.fillRect(p.x - bw / 2, p.y - r - 20, bw * Math.min(1, sh / 6), 2);
  }

  const label = u.kind === "minion" || u.kind === "spirit" ? cls.name : u.name;
  fillText(ctx, label, p.x, p.y - r - 26, u.isPlayer ? 12 : 10, u.isPlayer ? "#fff6d8" : "#d7d0c0", "center", "700");

  const pips = u.statuses.slice(0, 8);
  pips.forEach((st, i) => {
    const def = STATUSES[st.id];
    const px = p.x - ((pips.length - 1) * 8) / 2 + i * 8;
    const py = p.y + r + 8;
    ctx.beginPath();
    ctx.arc(px, py, 4.2, 0, Math.PI * 2);
    ctx.fillStyle = def.color;
    ctx.fill();
  });
  ctx.globalAlpha = 1;
  void world;
}

function drawTargetLock(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number, ppu: number): void {
  const player = world.units.find((u) => u.id === world.playerId);
  if (!player || player.autoTargetId == null) return;
  const t = world.units.find((u) => u.id === player.autoTargetId);
  if (!t || !t.pos) return;
  const p = toS(t.pos.x, t.pos.y, cam, viewW, viewH);
  ctx.beginPath();
  ctx.arc(p.x, p.y, t.radius * ppu + 8, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(240, 226, 168, 0.9)";
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 4]);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawEvents(ctx: CanvasRenderingContext2D, world: World, cam: Camera, viewW: number, viewH: number): void {
  for (const e of world.events) {
    const p = toS(e.x, e.y - e.age * 0.7, cam, viewW, viewH);
    const a = 1 - e.age / 0.95;
    ctx.globalAlpha = Math.max(0, a);
    if (e.kind === "dmg") fillText(ctx, `-${e.value}`, p.x, p.y, 13, "#ffb4b4", "center", "800");
    else if (e.kind === "heal") fillText(ctx, `+${e.value}`, p.x, p.y, 13, "#9eefb0", "center", "800");
    else if (e.kind === "crit") fillText(ctx, e.text || `CRIT ${e.value}`, p.x, p.y, 13, "#ffe08a", "center", "800");
    else fillText(ctx, e.text || e.kind.toUpperCase(), p.x, p.y, 11, "#f0e6c8", "center", "800");
    ctx.globalAlpha = 1;
  }
}

function fmtTime(world: World): string {
  if (world.suddenDeath) return "SUDDEN";
  const left = Math.max(0, world.duration - world.time);
  const m = Math.floor(left / 60);
  const s = Math.floor(left % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function drawChrome(ctx: CanvasRenderingContext2D, world: World, viewW: number, viewH: number, showBoard: boolean): void {
  const player = world.units.find((u) => u.id === world.playerId);
  rr(ctx, viewW / 2 - 160, 10, 320, 52, 10);
  ctx.fillStyle = "rgba(10,12,16,0.72)";
  ctx.fill();
  fillText(ctx, String(Math.floor(world.scores[0])), viewW / 2 - 80, 36, 26, TEAM[0], "center", "800");
  fillText(ctx, fmtTime(world), viewW / 2, 28, 16, world.suddenDeath ? "#ffdf7a" : "#f0ebe0", "center", "700");
  fillText(ctx, `first to ${SCORE_LIMIT}`, viewW / 2, 46, 10, "#8b93a0", "center");
  fillText(ctx, String(Math.floor(world.scores[1])), viewW / 2 + 80, 36, 26, TEAM[1], "center", "800");

  if (!player) return;
  drawSkillBar(ctx, player, viewW, viewH);

  if (showBoard) {
    rr(ctx, viewW / 2 - 220, 72, 440, 188, 10);
    ctx.fillStyle = "rgba(10,12,16,0.88)";
    ctx.fill();
    fillText(ctx, "FIELD", viewW / 2, 88, 12, "#c9a227", "center", "700");
    const rows = [...world.units].filter((u) => u.kind === "hero" || (u.kind === "corpse" && CLASSES[u.classId].playable)).sort((a, b) => a.team - b.team || b.damageDealt - a.damageDealt);
    rows.forEach((u, i) => {
      const y = 110 + i * 20;
      fillText(ctx, `${u.name}  ${CLASSES[u.classId].name}`, viewW / 2 - 200, y, 12, TEAM[u.team], "left");
      fillText(ctx, `${Math.round(u.damageDealt)} dmg`, viewW / 2 + 80, y, 12, "#d7d0c0", "left");
      fillText(ctx, `${Math.round(u.healsDone)} heal`, viewW / 2 + 160, y, 12, "#9eefb0", "left");
    });
  }
}

function drawSkillBar(ctx: CanvasRenderingContext2D, player: Unit, viewW: number, viewH: number): void {
  const slot = 72;
  const gap = 10;
  const total = 3 * slot + 2 * gap;
  const x0 = (viewW - total) / 2;
  const y = viewH - 108;
  const cls = CLASSES[player.classId];
  const auto = ABILITIES[autoFor(player.classId, player.state.weapon ?? 0)];
  const skill = ABILITIES[cls.skill];
  const ult = ABILITIES[cls.ult];
  const items = [
    { def: auto, cd: player.basicCd, max: player.basicCdMax, key: "AUTO", label: player.classId === "assassin" ? ((player.state.weapon ?? 0) === 1 ? "Sword" : "Kunai") : "Auto" },
    { def: skill, cd: player.skillCd, max: player.skillCdMax, key: bindLabel(binds.skill), label: "Skill" },
    { def: ult, cd: player.ultCd, max: player.ultCdMax, key: bindLabel(binds.ult), label: "Ult" },
  ];

  rr(ctx, x0 - 16, y - 40, total + 32, 132, 12);
  ctx.fillStyle = "rgba(10,12,16,0.78)";
  ctx.fill();

  const hpW = total;
  ctx.fillStyle = "#1a2430";
  ctx.fillRect(x0, y - 26, hpW, 8);
  ctx.fillStyle = "#5ad48a";
  ctx.fillRect(x0, y - 26, hpW * (player.hp / player.maxHp), 8);
  const sh = shieldTotal(player);
  if (sh > 0) {
    ctx.fillStyle = "#7ec8ff";
    ctx.fillRect(x0, y - 30, hpW * Math.min(1, sh / 8), 3);
  }
  fillText(ctx, `${Math.ceil(player.hp)}/${player.maxHp}`, x0 + hpW + 8, y - 22, 11, "#c8c2b4", "left");
  if (player.classId === "paladin") {
    fillText(ctx, `CHG ${Math.round((player.state.charge ?? 0) * 100)}%`, x0, y - 8, 10, "#e8b84a", "left");
  }
  if (player.classId === "ranger" && (player.state.poisonCharges ?? 0) > 0) {
    fillText(ctx, `POISON ×${player.state.poisonCharges}`, x0 + 90, y - 8, 10, "#6bbf3a", "left");
  }

  items.forEach((it, i) => {
    const x = x0 + i * (slot + gap);
    rr(ctx, x, y, slot, slot, 8);
    ctx.fillStyle = i === 2 ? "#2c220c" : "#1b222c";
    ctx.fill();
    ctx.strokeStyle = i === 2 ? "#d4a017" : cls.color;
    ctx.lineWidth = it.cd <= 0 ? 2 : 1;
    ctx.stroke();
    fillText(ctx, it.key, x + 8, y + 10, 10, "#8b93a0", "left", "700");
    const words = it.def.name.split(" ");
    if (words.length > 1) {
      fillText(ctx, words[0], x + slot / 2, y + 28, 11, "#f0ebe0", "center", "700");
      fillText(ctx, words.slice(1).join(" "), x + slot / 2, y + 42, 11, "#f0ebe0", "center", "700");
    } else {
      fillText(ctx, it.def.name, x + slot / 2, y + 34, 12, "#f0ebe0", "center", "700");
    }
    if (it.cd > 0 && it.max > 0) {
      const t = Math.min(1, it.cd / it.max);
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(x, y + slot * (1 - t), slot, slot * t);
      fillText(ctx, it.cd.toFixed(1), x + slot / 2, y + slot / 2 + 12, 11, "#fff", "center");
    }
  });

  fillText(
    ctx,
    `${cls.name}   ·   WASD move   ·   click to lock target   ·   ${bindLabel(binds.skill)} skill   ·   ${bindLabel(binds.ult)} ult   ·   ${bindLabel(binds.tuner)} tuner`,
    viewW / 2,
    viewH - 16,
    12,
    "#c8c2b4",
    "center",
  );
}

function drawMinimap(ctx: CanvasRenderingContext2D, world: World, viewW: number, _viewH: number): void {
  const mw = 154;
  const mh = (mw * ARENA_H) / ARENA_W;
  const x = viewW - mw - 16;
  const y = 16;
  rr(ctx, x - 6, y - 6, mw + 12, mh + 12, 8);
  ctx.fillStyle = "rgba(10,12,16,0.75)";
  ctx.fill();
  ctx.fillStyle = "#1e2630";
  ctx.fillRect(x, y, mw, mh);
  const sx = mw / ARENA_W;
  const sy = mh / ARENA_H;
  for (const s of world.stones) {
    ctx.beginPath();
    ctx.arc(x + s.pos.x * sx, y + s.pos.y * sy, 5, 0, Math.PI * 2);
    ctx.fillStyle = s.owner === null ? "#c9a227" : TEAM[s.owner];
    ctx.fill();
  }
  for (const c of world.cover) {
    if (c.destroyed) continue;
    ctx.fillStyle = "#6b5344";
    ctx.fillRect(x + c.x * sx, y + c.y * sy, c.w * sx, c.h * sy);
  }
  for (const u of world.units) {
    if (u.dead && u.kind !== "corpse") continue;
    ctx.beginPath();
    ctx.arc(x + u.pos.x * sx, y + u.pos.y * sy, u.isPlayer ? 3.4 : u.kind === "spirit" ? 1.6 : 2.4, 0, Math.PI * 2);
    ctx.fillStyle = u.kind === "spirit" ? "#9fd8e4" : TEAM[u.team];
    ctx.fill();
    if (u.isPlayer) {
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
  const held = world.stones.map((s) => {
    const [a, b] = occupants(world, s);
    return `${s.name[0]}${s.owner === null ? "·" : s.owner === 0 ? "A" : "B"}${a + b ? "*" : ""}`;
  });
  fillText(ctx, held.join("  "), x + mw / 2, y + mh + 18, 10, "#9aa4b2", "center");
}

function renderResult(ctx: CanvasRenderingContext2D, frame: RenderFrame, world: World): void {
  const { viewW, viewH } = frame;
  ctx.fillStyle = "rgba(8,10,14,0.62)";
  ctx.fillRect(0, 0, viewW, viewH);
  const player = world.units.find((u) => u.id === world.playerId);
  const win = world.winner === (player?.team ?? 0);
  rr(ctx, viewW / 2 - 220, viewH / 2 - 130, 440, 280, 16);
  ctx.fillStyle = "#141a22";
  ctx.fill();
  ctx.strokeStyle = win ? "#d4a017" : "#ff5a5a";
  ctx.lineWidth = 2;
  ctx.stroke();
  fillText(ctx, win ? "COURT HELD" : world.winner === null ? "UNRESOLVED" : "COURT LOST", viewW / 2, viewH / 2 - 88, 28, win ? "#f2e6c4" : "#ffb4b4", "center", "800");
  fillText(ctx, `${Math.floor(world.scores[0])}   —   ${Math.floor(world.scores[1])}`, viewW / 2, viewH / 2 - 44, 32, "#f0ebe0", "center", "800");
  fillText(
    ctx,
    world.endedReason === "sudden" ? "Decided in sudden death on the Heart stone." : world.endedReason === "clock" ? "The court closed." : "A Wardstone tally reached 100.",
    viewW / 2,
    viewH / 2 - 8,
    13,
    "#9aa4b2",
    "center",
  );
  if (player) {
    fillText(
      ctx,
      `${Math.round(player.damageDealt)} dealt   ·   ${Math.round(player.healsDone)} mended   ·   ${CLASSES[player.classId].name}`,
      viewW / 2,
      viewH / 2 + 28,
      13,
      "#c8c2b4",
      "center",
    );
  }
  rr(ctx, (viewW - 220) / 2, viewH / 2 + 92, 220, 44, 10);
  ctx.fillStyle = "#d4a017";
  ctx.fill();
  fillText(ctx, "PICK AGAIN", viewW / 2, viewH / 2 + 114, 16, "#1a1408", "center", "800");
}
